import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Switch,
  Typography,
  Space,
  message,
  InputNumber,
  Divider,
  Tag,
  Row,
  Col,
} from 'antd';
import { SaveOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

const PopupConfig = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [features, setFeatures] = useState([]);
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    fetchPopup();
  }, []);

  const fetchPopup = async () => {
    setLoading(true);
    try {
      const response = await api.get('/popup/admin');
      if (response.data.success && response.data.data.popup) {
        const popup = response.data.data.popup;
        form.setFieldsValue(popup);
        
        const featureList = typeof popup.features === 'string' 
          ? JSON.parse(popup.features || '[]') 
          : (popup.features || []);
        setFeatures(featureList);
      }
    } catch (error) {
      message.error('获取弹窗配置失败');
      console.error('Fetch popup error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();
      values.features = features;
      
      await api.put('/popup', values);
      message.success('弹窗配置已保存');
    } catch (error) {
      message.error('保存失败');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    if (newFeature.trim() && !features.includes(newFeature.trim())) {
      setFeatures([...features, newFeature.trim()]);
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4}>弹窗配置</Title>
        <Button 
          type="primary" 
          icon={<SaveOutlined />} 
          onClick={handleSave}
          loading={saving}
        >
          保存配置
        </Button>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card title="弹窗内容" loading={loading}>
            <Form form={form} layout="vertical">
              {/* Basic Settings */}
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="isEnabled" label="启用弹窗" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="showOnce" label="仅显示一次" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="delaySeconds" label="延迟显示（秒）">
                <InputNumber min={0} max={30} style={{ width: 120 }} />
              </Form.Item>

              <Divider>标题区域</Divider>

              <Form.Item name="title" label="主标题" rules={[{ required: true }]}>
                <Input placeholder="GARY JOE BARTAK - CHIEF ANALYST" />
              </Form.Item>

              <Form.Item name="subtitle" label="副标题">
                <TextArea rows={2} placeholder="Dedicated to providing professional trading strategy analysis..." />
              </Form.Item>

              <Divider>统计数据</Divider>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="totalProfit" label="总收益">
                    <Input placeholder="+$5,330,800" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="monthlyProfit" label="月收益">
                    <Input placeholder="+$422,263" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="activeTrades" label="活跃交易">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider>公告区域</Divider>

              <Form.Item name="announcementTitle" label="公告标题">
                <Input placeholder="Important Announcement" />
              </Form.Item>

              <Form.Item name="announcementSubtitle" label="公告副标题">
                <Input placeholder="Discover GARY JOE BARTAK verified stock trading record" />
              </Form.Item>

              <Form.Item name="announcementContent" label="公告内容">
                <TextArea rows={4} placeholder="公告详细内容..." />
              </Form.Item>

              <Divider>特性列表</Divider>

              <div style={{ marginBottom: 16 }}>
                <Space wrap>
                  {features.map((feature, index) => (
                    <Tag 
                      key={index} 
                      closable 
                      onClose={() => removeFeature(index)}
                      color="blue"
                    >
                      {feature}
                    </Tag>
                  ))}
                </Space>
              </div>

              <Space.Compact style={{ width: '100%' }}>
                <Input 
                  placeholder="添加新特性..."
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onPressEnter={addFeature}
                />
                <Button type="primary" onClick={addFeature}>添加</Button>
              </Space.Compact>

              <Divider>按钮设置</Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="buttonText" label="按钮文字">
                    <Input placeholder="JOIN EXCLUSIVE COMMUNITY NOW" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="buttonLink" label="按钮链接">
                    <Input placeholder="/register" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="预览说明">
            <Space direction="vertical">
              <Text type="secondary">
                弹窗将在用户访问网站时自动显示（根据配置的延迟时间）
              </Text>
              <Text type="secondary">
                如果启用"仅显示一次"，用户关闭后不会再次看到弹窗
              </Text>
              <Text type="secondary">
                按钮链接可以是站内路径（如 /register）或完整URL
              </Text>
              <Divider />
              <Button 
                icon={<EyeOutlined />} 
                onClick={() => window.open(process.env.REACT_APP_FRONTEND_URL || '/', '_blank')}
              >
                在前端预览
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </Space>
  );
};

export default PopupConfig;

